import * as React from 'react'

import BookmarkButton, { Style as BookmarkButtonStyle } from 'main/components/lead/BookmarkButton'
import { PhilosophyTag } from 'main/components/profile'
import { useWhitelabel } from 'main/framework'
import { Link, Strong } from 'main/ui'
import { AssetName } from 'main/util/asset'

import {
  BookmarkContainer,
  Container,
  InfoContainer,
  PhotoContainer,
  SchoolNameContainer,
  StyledPhilosophyTag,
  SubInfoContainer,
  UpcomingEventContainer,
} from './Callout.styles'
import {
  ActionLinks,
  Hours,
  Photo,
  Pricing,
  ProgramType,
  RatingDisplay,
  UpcomingEventBadge,
  WSVerifiedIcon,
} from './common'
import NetworkRating from './common/NetworkRating'
import NetworkRatingV2 from './common/NetworkRatingV2'
import { ILeadStatus, Target, UpcomingEvent } from './types'
import {
  getBookmarkButtonSchoolIdForTarget,
  getDataForTarget,
  getDisplayedUpcomingEvent,
} from './util'

interface IProps {
  target: Target
  leadStatus?: ILeadStatus
  networkLogo?: string
  upcomingEvent: UpcomingEvent | null
  coverImageUrl?: string
  showWeeklyPricesOnly?: boolean
}

export default function Callout(props: IProps) {
  const { target, leadStatus, upcomingEvent, coverImageUrl, showWeeklyPricesOnly = false } = props
  const displayedUpcomingEvent = getDisplayedUpcomingEvent(leadStatus || null, upcomingEvent)
  const bookmarkButtonSchoolId = getBookmarkButtonSchoolIdForTarget(target)
  const FORMATED_IMAGE = `${coverImageUrl}/-/format/auto/-/quality/lightest/-/scale_crop/960x540/center/`

  const {
    hours,
    isVerified,
    name,
    pagePath,
    philosophy,
    pricing,
    rating,
    programType,
    networkRating,
    whitelabelRating,
  } = getDataForTarget(target)

  const whitelabel = useWhitelabel()
  const networkLogo = whitelabel.getAsset(AssetName.LOGO_SMALL)

  const isExternalSchool = 'externalSchool' in target && target.externalSchool.id
  const isExternalListing = 'listing' in target && !target.listing.schoolId

  return (
    <Container>
      <PhotoContainer>
        <Link
          to={pagePath}
          target="_blank"
          rel={isExternalSchool || isExternalListing ? 'nofollow' : ''}
        >
          <Photo url={FORMATED_IMAGE} />
          {displayedUpcomingEvent && (
            <UpcomingEventContainer>
              <UpcomingEventBadge upcomingEvent={displayedUpcomingEvent} />
            </UpcomingEventContainer>
          )}
          <StyledPhilosophyTag>
            {philosophy && <PhilosophyTag philosophy={philosophy} />}
          </StyledPhilosophyTag>
        </Link>
        {bookmarkButtonSchoolId && (
          <BookmarkContainer>
            <BookmarkButton schoolId={bookmarkButtonSchoolId} style={BookmarkButtonStyle.Dark} />
          </BookmarkContainer>
        )}
      </PhotoContainer>

      <InfoContainer>
        <Link to={pagePath} target="_blank">
          <SchoolNameContainer>
            {isVerified && renderVerifiedIcon()}
            <Strong preferredColor={whitelabel.listingNameColor(target)}>{name}</Strong>
          </SchoolNameContainer>
          <SubInfoContainer>
            {pricing && (
              <Pricing
                min={pricing.min}
                max={pricing.max}
                showWeeklyPricesOnly={showWeeklyPricesOnly}
                weeklyMin={pricing.weeklyMin}
                weeklyMax={pricing.weeklyMax}
              />
            )}
          </SubInfoContainer>
          <SubInfoContainer>
            {hours && <Hours start={hours.start} end={hours.end} />}
          </SubInfoContainer>
          <SubInfoContainer>
            {programType && <ProgramType programType={programType} />}
          </SubInfoContainer>
          {whitelabel.showByDefaultExceptIfFalse(
            AssetName.WONDERSCHOOL_RATING_DISPLAY__SEARCH__CALLOUT,
          ) &&
            rating &&
            rating.overall && (
              <RatingDisplay value={rating.overall} size={13} count={rating.count} />
            )}

          {/* This will go away once ELI has been migrated to the new NetworkRatings*/}
          {whitelabel.hideByDefaultExceptIfTrue(
            AssetName.NETWORK_RATING_DISPLAY__SEARCH__CALLOUT,
          ) && <NetworkRating networkRating={networkRating} />}

          {whitelabel.hideByDefaultExceptIfTrue(AssetName.NETWORK_RATING_FILTERS_V2) && (
            <NetworkRatingV2 whitelabelRating={whitelabelRating} />
          )}
        </Link>

        {leadStatus && (
          <ActionLinks
            lead={leadStatus.lead}
            onRequestOpenScheduleTourModal={leadStatus.onRequestOpenScheduleTourModal}
            onRequestOpenCancelTourRequestModal={leadStatus.onRequestOpenCancelTourRequestModal}
          />
        )}
      </InfoContainer>
    </Container>
  )

  function renderVerifiedIcon() {
    if (networkLogo) return <img src={`${networkLogo}-/resize/25x/-/quality/smart/`} />

    return <WSVerifiedIcon />
  }
}

Callout.defaultProps = {
  upcomingEvent: null,
}
